import {
  PercentageOutlined
} from "@ant-design/icons";
import {
  Col,
  Form,
  Input,
  Spin,
  Upload,
  message
} from "antd";
import "antd/dist/antd.min.css";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  getRequest,
  patchRequest,
  postRequest
} from "../../../axios";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import InputField from "../../../common/htmlComponents/InputField";
import { ReactComponent as About } from "../../../image/About.svg";
import { ReactComponent as Name } from "../../../image/Name.svg";
import "../../../styles/Main.scss";

import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import CategoryImageUpload from "./CategoryImageUpload";

const AddCategory = ({
  handleCancel,
  zoneList,
  setReload,
  categoryTreeId,
  modalKey,
  setFileList,
  fileList,
  setStateM,
}) => {
  const { t } = useTranslation();
  const formRef = useRef();

  // State initialization
  const initialState = {
    name: null,
    description: null,
    gst_rate: null,
  };
  const [state, setState] = useState(initialState);
  const [btnLoading, setBtnLoading] = useState(false);
  const [apiLoading, setapiLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState("");
  const [fileLink, setFileLink] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [size, setSize] = useState(null);
  const [identifyProfileUrl, setIdentifyProfileUrl] = useState(null);
  const [uploadFileProfilePic, setUploadFileProfilePic] = useState("");

  const [deleteFlag, setDeleteFlag] = useState(0);

  // Fetch category details by ID
  useEffect(() => {
    const categoryGetById = async () => {

      try {
        setapiLoading(true);
        const apiResponse = await getRequest(
          `category-tree/${categoryTreeId}/`
        );
        if (apiResponse.data.success) {
          setState(apiResponse.data.data);
          setFileLink(apiResponse.data.data.image);
          formRef.current.setFieldsValue({
            ...apiResponse.data.data,
          });
          const url = apiResponse.data.data.image;
          setIdentifyProfileUrl(apiResponse.data.data.image);

          if (url !== null) {
            setFileList([
              {
                url: url,
              },
            ]);
          }
          setapiLoading(false);
        } else {
          ErrorNotificationMsg(t("error_in_list_category"));
        }
      } catch (error) {
        setapiLoading(false);
      }
    };

    if (categoryTreeId !== null) {
      categoryGetById();
    }
  }, [categoryTreeId, modalKey, setFileList, t]);

  // Handle Escape key to navigate back 
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        backToList();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const url = state.image;

  let filename = "";
  try {
    if (fileLink) {
      filename = url.split("/").pop();
    }
  } catch (e) {
    filename = "";
  }

  // Handle input changes
  const handleInputChange = (e) => {
    let { name, value } = e.target;
    setState({ ...state, [name]: value });
  };
  const handleChange = (name, value) => {
    setState({ ...state, [name]: value });
  };

  // Validate file upload
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      return Upload.LIST_IGNORE;
    }
    // Clear error message if file type is correct
    return false; // Prevent automatic upload, will handle manually
  };

  // Upload props for the file
  const uploadProps = {
    accept: ".jpg, .png, .jpeg",
    showUploadList: false,
    beforeUpload: (file) => {
      setSize(file.size);
      if (file.size <= 2097152) {
        setUploadFile(file);
        setFileName(file.name);
      }
      return false;
    },
  };

  // Navigate back to the list
  const backToList = () => {
    setState(initialState);
    formRef.current.setFieldsValue(initialState);
    handleCancel();
  };

  // Handle form submission
  const handleSubmit = async () => {
    setBtnLoading(true);

    const payload = {
      name: state.name,
      description: state.description,
      gst_rate: state.gst_rate,
      status: "active",
    };
    try {
      let apiResponse;

      let customerHeaders = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      const formData = new FormData();

      formData.append("image", uploadFileProfilePic);
      formData.append("form_data", JSON.stringify(payload));
      if (categoryTreeId === null) {
        apiResponse = await postRequest(
          "category-tree/",
          formData,
          customerHeaders
        );
      } else {
        apiResponse = await patchRequest(
          `category-tree/${categoryTreeId}/`,
          formData,
          customerHeaders
        );
      }
      if (apiResponse.data.success) {
        SuccessNotificationMsg(
          categoryTreeId === null
            ? t("category_added_successfully")
            : t("category_update_successfully")
        );
        setState(initialState);
        setDeleteFlag(0);
        setStateM((prev) => ({
          ...prev,
          page: 1,
        }));
        formRef.current.setFieldsValue(initialState);
        setBtnLoading(false);
        handleCancel();
        setReload((reload) => !reload);
      }
      setBtnLoading(false);
    } catch (error) {
      setBtnLoading(false);
    }
  };
  return (
    <>
      <section className="grid-sec FormSection Container">
        <div className="FormBody">
          <Spin spinning={apiLoading}>
            <Form onFinish={handleSubmit} autoComplete="off" ref={formRef}>
              <Col md={24} className="TextFeild">
                <CategoryImageUpload
                  deleteFlag={deleteFlag}
                  setDeleteFlag={setDeleteFlag}
                  setUploadFileProfilePic={setUploadFileProfilePic}
                  fileList={fileList}
                  setFileList={setFileList}
                  identifyProfileUrl={identifyProfileUrl}
                  setIdentifyProfileUrl={setIdentifyProfileUrl}
                  categoryTreeId={categoryTreeId}
                  beforeUpload={beforeUpload}
                />
              </Col>
              <Col md={24} className="TextFeild">
                <InputField
                  name="name"
                  label={t("label_category_name")}
                  rules={t("val_category_name")}
                  placeholder={t("pl_category_name")}
                  handleInputChange={handleInputChange}
                  prefix={<Name />}
                  pattern={"^[a-zA-Z0-9 ]+$"}
                  max={25}
                />
              </Col>
              <Col md={24} className="TextFeild">
                <InputField
                  name="description"
                  label={t("label_description")}
                  rules={t("val_description")}
                  placeholder={t("pl_description")}
                  handleInputChange={handleInputChange}
                  prefix={<About />}
                  max={30}
                />
              </Col>
              <Col md={24} className="TextFeild">
                <Form.Item
                  label={t("label_gst2")}
                  name="gst_rate"
                  rules={[
                    {
                      required: true,
                      message: <Trans>{t("pl_gst")}</Trans>,
                    },
                    {
                      pattern: /^(100|[1-9]?[0-9])$/,
                      message: "Enter in range of 1-100",
                    },
                  ]}

                >
                  <Input
                    type="number"
                    placeholder={t("pl_gst")}
                    onKeyPress={(e) => {
                      if (e.key === "." || e.key === "e") {
                        e.preventDefault(); // Prevent entering a dot
                      }
                    }}
                    onChange={(e) => handleChange("gst_rate", e.target.value)}
                    prefix={<PercentageOutlined />}
                  />
                </Form.Item>
              </Col>


              <div className="FormFooter">
                <ButtonField
                  type="primary"
                  className="CancelGradiantBtn"
                  htmlType="button"
                  label={t("cancel")}
                  onClick={backToList}
                />

                <ButtonField
                  type="primary"
                  className="SaveGradiantBtn"
                  htmlType="Submit"
                  label={t("save")}
                  loading={btnLoading}
                />
              </div>
            </Form>
          </Spin>
        </div>
      </section>
    </>
  );
};
AddCategory.propTypes = {
  handleCancel: PropTypes.func,
  zoneList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      zone_name: PropTypes.string,
    })
  ),
  setReload: PropTypes.func,
  categoryTreeId: PropTypes.number,
  modalKey: PropTypes.bool,
  fileList: PropTypes.array,
  setFileList: PropTypes.func,
  setStateM: PropTypes.func,
};

export default AddCategory;
